import { emeAddToCart } from '@theme/eme-cart';

/**
 * @typedef {{ id: number, options: string[], available: boolean, price: string, compare: string | null, media: number }} EmeVariant
 */

/**
 * Studio product page: variant selection, gallery navigation, zoom, size chart (with IN/CM toggle),
 * description tabs, add to cart and "complete the look" recommendations.
 */
class EmeProduct extends HTMLElement {
  /** @type {EmeVariant[]} */
  #variants = [];

  /** @type {string[]} */
  #selected = [];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const data = this.querySelector('script[data-variants]');
    this.#variants = data ? JSON.parse(data.textContent || '[]') : [];

    const input = this.#ref('variantInput');
    const current = this.#variants.find((variant) => String(variant.id) === input?.value) ?? this.#variants[0];
    this.#selected = current ? [...current.options] : [];

    this.addEventListener('click', this.#onClick);
    this.querySelector('form.eme-pdp__form')?.addEventListener('submit', this.#onSubmit);

    this.#observeMedia();
    this.#refreshAvailability();
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  /**
   * @param {string} name
   * @returns {any}
   */
  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const option = /** @type {HTMLElement | null} */ (target.closest('[data-option-index][data-value]'));
    if (option) {
      this.#selectOption(Number(option.dataset.optionIndex), option.dataset.value ?? '');
      return;
    }

    const thumb = /** @type {HTMLElement | null} */ (target.closest('[data-media-target]'));
    if (thumb) {
      this.#scrollToMedia(thumb.dataset.mediaTarget ?? '');
      return;
    }

    const zoom = /** @type {HTMLElement | null} */ (target.closest('[data-zoom]'));
    if (zoom) {
      const dialog = this.#ref('zoomDialog');
      const image = this.#ref('zoomImage');
      if (dialog && image) {
        image.src = zoom.dataset.zoom;
        dialog.showModal();
      }
      return;
    }

    const tab = /** @type {HTMLElement | null} */ (target.closest('[role="tab"]'));
    if (tab) {
      for (const sibling of tab.parentElement?.querySelectorAll('[role="tab"]') ?? []) {
        const selected = sibling === tab;
        sibling.setAttribute('aria-selected', String(selected));
        const panel = document.getElementById(sibling.getAttribute('aria-controls') ?? '');
        if (panel) panel.hidden = !selected;
      }
      return;
    }

    const unit = /** @type {HTMLElement | null} */ (target.closest('[data-size-unit]'));
    if (unit) {
      const dialog = unit.closest('dialog');
      if (dialog) dialog.dataset.unit = unit.dataset.sizeUnit;
      for (const button of unit.parentElement?.querySelectorAll('[data-size-unit]') ?? []) {
        button.setAttribute('aria-pressed', String(button === unit));
      }
      return;
    }

    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-open-dialog]'));
    if (opener) {
      const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(opener.dataset.openDialog ?? ''));
      dialog?.showModal();
      return;
    }

    // Close buttons, a click on the zoom image, or a backdrop click close the open dialog
    const dialog = target.closest('dialog');
    if (dialog && (target.closest('[data-close-dialog]') || target === dialog || dialog === this.#ref('zoomDialog'))) {
      dialog.close();
    }
  };

  /**
   * @param {number} index
   * @param {string} value
   */
  #selectOption(index, value) {
    this.#selected[index] = value;

    // Keep the other choices when that combination exists, otherwise fall back to any variant with this value
    let variant = this.#variants.find((v) => v.options.every((opt, i) => opt === this.#selected[i]));
    if (!variant) {
      variant =
        this.#variants.find((v) => v.options[index] === value && v.available) ??
        this.#variants.find((v) => v.options[index] === value);
      if (variant) this.#selected = [...variant.options];
    }

    this.#updateOptionButtons();
    this.#refreshAvailability();
    this.#applyVariant(variant);
  }

  #updateOptionButtons() {
    for (const button of this.querySelectorAll('[data-option-index][data-value]')) {
      const el = /** @type {HTMLElement} */ (button);
      const checked = this.#selected[Number(el.dataset.optionIndex)] === el.dataset.value;
      el.setAttribute('aria-checked', String(checked));
    }
  }

  /** Marks option values with no available variant for the other current choices */
  #refreshAvailability() {
    for (const button of this.querySelectorAll('[data-option-index][data-value]')) {
      const el = /** @type {HTMLElement} */ (button);
      const index = Number(el.dataset.optionIndex);
      const available = this.#variants.some(
        (v) =>
          v.available &&
          v.options[index] === el.dataset.value &&
          v.options.every((opt, i) => i === index || opt === this.#selected[i])
      );
      el.toggleAttribute('data-unavailable', !available);
    }
  }

  /** @param {EmeVariant | undefined} variant */
  #applyVariant(variant) {
    const input = this.#ref('variantInput');
    const button = this.#ref('addButton');
    const price = this.#ref('price');

    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.unavailableLabel;
      }
      return;
    }

    if (input) input.value = String(variant.id);

    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.addLabel : button.dataset.soldOutLabel;
    }

    if (price) {
      price.innerHTML = `${variant.compare ? `<s class="eme-pdp__compare">${variant.compare}</s>` : ''}<span>${variant.price}</span>`;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());

    if (variant.media) this.#scrollToMedia(String(variant.media));
  }

  /** @param {string} mediaId */
  #scrollToMedia(mediaId) {
    const figure = /** @type {HTMLElement | null} */ (this.querySelector(`[data-media-id="${mediaId}"]`));
    const images = this.#ref('images');
    if (!figure || !images) return;

    const isSlider = getComputedStyle(images).overflowX === 'auto';
    if (isSlider) {
      images.scrollTo({ left: figure.offsetLeft - images.offsetLeft, behavior: 'smooth' });
    } else {
      figure.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /** Highlights the thumbnail and dot of the image in view */
  #observeMedia() {
    const figures = this.querySelectorAll('[data-media-id]');
    if (figures.length < 2) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = /** @type {HTMLElement} */ (entry.target).dataset.mediaId;

          for (const thumb of this.querySelectorAll('[data-media-target]')) {
            const isActive = /** @type {HTMLElement} */ (thumb).dataset.mediaTarget === id;
            if (isActive) {
              thumb.setAttribute('aria-current', 'true');
              thumb.scrollIntoView({ block: 'nearest' });
            } else {
              thumb.removeAttribute('aria-current');
            }
          }

          for (const dot of this.querySelectorAll('[data-media-dot]')) {
            dot.toggleAttribute('data-active', /** @type {HTMLElement} */ (dot).dataset.mediaDot === id);
          }
        }
      },
      { threshold: 0.6 }
    );

    for (const figure of figures) this.#observer.observe(figure);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const button = this.#ref('addButton');
    const input = this.#ref('variantInput');
    const error = this.#ref('error');
    if (!button || !input || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    try {
      await emeAddToCart(input.value, 1, this);
      const label = button.textContent;
      button.textContent = window.Theme?.translations?.added || 'Added';
      setTimeout(() => {
        button.textContent = label;
      }, 1500);
    } catch (err) {
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  async #loadRecommendations() {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendations-url]'));
    const target = this.#ref('look');
    if (!container || !target) return;

    try {
      const response = await fetch(container.dataset.recommendationsUrl ?? '');
      const html = await response.text();
      const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('.eme-pdp__look-grid');

      if (grid && grid.children.length > 0) {
        target.replaceChildren(grid);
        container.hidden = false;
      }
    } catch (err) {
      console.error(err);
    }
  }
}

if (!customElements.get('eme-product')) {
  customElements.define('eme-product', EmeProduct);
}
